import type { ReactElement, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Link from 'next/link';

import {
  APP_SUBTITLE_COLOR,
  APP_TITLE_COLOR,
} from './brandSurface';

export type PageHeaderBreadcrumb = {
  label: string;
  href?: string;
};

export type PageHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Chip or label rendered beside the title. */
  badge?: ReactNode;
  breadcrumbs?: PageHeaderBreadcrumb[];
  /** Larger title scale for primary product hubs. */
  size?: 'medium' | 'large';
  sx?: object;
};

/**
 * Standard in-app page header. Use inside MainLayout — do not add root padding
 * or nested Containers around this; gutters come from MainLayout.
 */
export default function PageHeader({
  title,
  subtitle,
  actions,
  badge,
  breadcrumbs,
  size = 'medium',
  sx,
}: PageHeaderProps): ReactElement {
  const titleVariant = size === 'large' ? 'headline-large' : 'headline-medium';

  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Grid
        container
        spacing={2}
        alignItems='flex-start'
        justifyContent='space-between'
      >
        <Grid item xs={12} md={actions ? true : 12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Typography
              variant={titleVariant}
              component='h1'
              sx={{ color: APP_TITLE_COLOR, lineHeight: 1.15 }}
            >
              {title}
            </Typography>
            {badge}
          </Box>
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <Breadcrumbs
              aria-label='breadcrumb'
              separator='>'
              sx={{ mt: 1 }}
            >
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                if (!isLast && crumb.href) {
                  return (
                    <MuiLink
                      key={`${crumb.label}-${index}`}
                      component={Link}
                      href={crumb.href}
                      underline='hover'
                      variant='body-small'
                      sx={{ color: APP_SUBTITLE_COLOR }}
                    >
                      {crumb.label}
                    </MuiLink>
                  );
                }
                return (
                  <Typography
                    key={`${crumb.label}-${index}`}
                    variant='body-small'
                    sx={{ color: APP_TITLE_COLOR }}
                  >
                    {crumb.label}
                  </Typography>
                );
              })}
            </Breadcrumbs>
          ) : null}
          {subtitle ? (
            <Typography
              variant='body-large'
              sx={{ color: APP_SUBTITLE_COLOR, mt: 1, maxWidth: 760 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Grid>
        {actions ? (
          <Grid
            item
            xs={12}
            md='auto'
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              alignItems: 'center',
            }}
          >
            {actions}
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}
